import localforage from "localforage";
import { defineStore } from "pinia";
import { HousingOrgRoleName } from "~/api";
import { authStore } from "~/stores/authStore";
import { apiClient } from "~/utils/apiClient";

export const useMyHousingOrgRolesStore = defineStore("MyHousingOrgRolesStore", {
  state: (): {
    rolesByHousingOrg: Record<string, HousingOrgRoleName[]>;
    storeLoaded: boolean;
    isRefreshing: boolean;
    lastLoadTime: number;
  } => {
    return {
      rolesByHousingOrg: {},
      storeLoaded: false,
      isRefreshing: false,
      lastLoadTime: 0,
    };
  },
  actions: {
    async load(forceUpdate: boolean = false) {
      if (!authStore.account) {
        return;
      }

      if (this.isRefreshing) {
        return new Promise<void>((resolve, _) => {
          const checkRefreshing = setInterval(() => {
            if (!this.isRefreshing) {
              clearInterval(checkRefreshing);
              resolve();
            }
          }, 100);
        });
      }

      this.isRefreshing = true;

      const MaxCacheTime = 10 * 60 * 1000;

      if (Date.now() - this.lastLoadTime > MaxCacheTime) {
        forceUpdate = true;
      }

      if (!forceUpdate && Object.keys(this.rolesByHousingOrg).length !== 0) {
        this.isRefreshing = false;
        return;
      }

      try {
        const cached = await localforage.getItem(this.$id);
        if (cached && !forceUpdate) {
          this.rolesByHousingOrg = JSON.parse(cached as string);
          this.storeLoaded = true;
          this.isRefreshing = false;
          return;
        }

        console.log("getting fresh", this.$id);

        this.rolesByHousingOrg =
          await apiClient.housingOrgRoles.getMyHousingOrgRoles();
        localforage.setItem(this.$id, JSON.stringify(this.rolesByHousingOrg));
        this.storeLoaded = true;
        this.isRefreshing = false;
        this.lastLoadTime = Date.now();
      } catch (ex) {
        this.rolesByHousingOrg = {};
        this.storeLoaded = false;
        this.isRefreshing = false;
        throw ex;
      }
    },
    clear() {
      this.rolesByHousingOrg = {};
      this.storeLoaded = false;
      localforage.removeItem(this.$id);
    },
    getRolesForHousingOrg(housingOrgGuid: string): HousingOrgRoleName[] {
      return this.rolesByHousingOrg[housingOrgGuid] ?? [];
    },
    hasRole(housingOrgGuid: string, role: HousingOrgRoleName): boolean {
      return this.getRolesForHousingOrg(housingOrgGuid).includes(role);
    },
  },
});
